/**
 * FontConfigService
 *
 * Service for managing project-level font configuration in Firestore.
 * Stores settings in: projects/{projectId}/settings/fontConfig
 *
 * Follows the same pattern as ProjectAnalyticsService.
 *
 * @module font-config/services
 */

import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ProjectFontConfig } from '../types/fontConfig.types';
import { DEFAULT_FONT_CONFIG } from '../types/fontConfig.types';

/**
 * FontConfigService
 *
 * Manages font configuration for a specific project.
 * Settings are stored in Firestore at: projects/{projectId}/settings/fontConfig
 */
export class FontConfigService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  /**
   * Get the Firestore document reference for project font config
   */
  private getDocRef(projectId: string) {
    return doc(this.firestore, 'projects', projectId, 'settings', 'fontConfig');
  }

  /**
   * Get font config for a project
   *
   * @returns Project font config or defaults if not configured
   */
  async getConfig(projectId: string): Promise<ProjectFontConfig> {
    try {
      const docRef = this.getDocRef(projectId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { ...DEFAULT_FONT_CONFIG };
      }

      const data = docSnap.data() as ProjectFontConfig;
      return {
        version: data.version ?? 1,
        platformMappings: data.platformMappings ?? {},
        fontLabels: data.fontLabels,
        customFonts: data.customFonts,
        updatedAt: data.updatedAt ?? '',
      };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FontConfigService.getConfig');
      throw error;
    }
  }

  /**
   * Save complete font config for a project
   */
  async saveConfig(projectId: string, config: ProjectFontConfig): Promise<void> {
    try {
      const docRef = this.getDocRef(projectId);
      // Firestore rejects any undefined values (including nested ones).
      // JSON round-trip strips all undefined fields at every depth.
      const data = JSON.parse(JSON.stringify({
        ...config,
        updatedAt: new Date().toISOString(),
      }));
      await setDoc(docRef, data, { merge: false });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'FontConfigService.saveConfig');
      throw error;
    }
  }

  /**
   * Subscribe to real-time font config updates
   *
   * @returns Unsubscribe function
   */
  subscribeToConfig(
    projectId: string,
    callback: (config: ProjectFontConfig) => void,
  ): Unsubscribe {
    const docRef = this.getDocRef(projectId);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          callback({ ...DEFAULT_FONT_CONFIG });
          return;
        }

        const data = docSnap.data() as ProjectFontConfig;
        callback({
          version: data.version ?? 1,
          platformMappings: data.platformMappings ?? {},
          fontLabels: data.fontLabels,
          customFonts: data.customFonts,
          updatedAt: data.updatedAt ?? '',
        });
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'FontConfigService.subscribeToConfig',
        );
      },
    );
  }
}

// Singleton
let serviceInstance: FontConfigService | null = null;

export function getFontConfigService(): FontConfigService {
  if (!serviceInstance) {
    serviceInstance = new FontConfigService();
  }
  return serviceInstance;
}
